<template>
  <div>
    <v-description v-if="status">
      <!-- Status first: type + occupancy tags, pencil opens the
           status-change modal (replaces the old "Mettre à jour" button) -->
      <v-description-item>
        <template #left>
          {{ $t("status").capitalize() }}
        </template>

        <template #right>
          <div class="is-flex is-align-items-center is-justify-content-space-between is-full py-1">
            <div>
              <span
                class="status-pill mr-2"
                :class="statusTagClass(status)"
              >
                <span v-if="status.type == 'in_renovation'">
                  {{ $t("in_renovation_" + status.in_renovation_status) }}
                </span>
                <span v-else>
                  {{ $t(status.type).capitalize() }}
                </span>
              </span>
            </div>
            <button
              v-if="mode === 'update'"
              class="button is-white is-small"
              @click="$emit('open-status-modal')"
            >
              <span class="icon">
                <i class="mdi mdi-pencil mdi-14px" />
              </span>
            </button>
          </div>
        </template>
      </v-description-item>

      <!-- Occupation: OCCUPÉ / VACANT -->
      <v-description-item>
        <template #left>
          {{ $t("occupation_label") }}
        </template>

        <template #right>
          <div class="py-1">
            <span
              class="status-pill"
              :class="status.is_property_occupied ? 'is-occupied' : 'is-vacant'"
            >
              {{ status.is_property_occupied ? $t("occupied") : $t("not_occupied") }}
            </span>
          </div>
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("asked_rent").capitalize() }}
        </template>

        <template #right>
          <div class="is-flex is-align-items-center is-justify-content-space-between is-full py-1">
            <span class="mr-2">{{ status.asked_rent }} $</span>
            <div class="is-flex is-align-items-center">
              <button
                v-if="$roleCan([$laravel.role_admin])"
                class="button is-small"
                @click="onPriceRevisionBtnClick"
              >
                <span>{{ $t('price_revision') }}</span>
              </button>
              <!-- Pencil last so it lines up with the other rows' pencils -->
              <button
                v-if="mode === 'update'"
                class="button is-white is-small"
                @click="onClick({ label: $t('asked_rent').capitalize(), field: 'asked_rent', value: status.asked_rent })"
              >
                <span class="icon">
                  <i class="mdi mdi-pencil mdi-14px" />
                </span>
              </button>
            </div>
          </div>
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("promotion").capitalize() }}
        </template>

        <template #right>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between is-full py-1"
          >
            <span
              class="mr-2"
              style="white-space: pre-line"
            >{{ promotionSummary }}</span>
            <button
              v-if="mode === 'update'"
              class="button is-white is-small"
              @click="onOpenPromotionModal"
            >
              <span class="icon">
                <i class="mdi mdi-pencil mdi-14px" />
              </span>
            </button>
          </div>
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("availability_date_show").capitalize() }}
        </template>

        <template #right>
          <v-field
            field="availability_date"
            :label="$t('availability_date_show').capitalize()"
            :value="status.availability_date"
            :mode="mode"
            type="date"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("status_possible_lease_duration").capitalize() }}
        </template>

        <template #right>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between is-full"
          >
            <div>
              <!-- One duration per line, "12 mois ;" then the next -->
              <div>
                <p
                  v-for="(line, i) in leaseDurationLines"
                  :key="i"
                >
                  {{ line }}{{ i < leaseDurationLines.length - 1 ? " ;" : "" }}
                </p>
              </div>
            </div>
            <button
              v-if="
                ($roleCan([$laravel.role_admin, $laravel.role_client]) ||
                  userExistInRentAgent($laravel.user.id, status)) &&
                  mode == 'update'
              "
              class="button is-white is-small"
              @click="onOpenPossibleLeaseDurationModal"
            >
              <span class="icon">
                <i class="mdi mdi-pencil mdi-14px" />
              </span>
            </button>
          </div>
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("key_access").capitalize() }}
        </template>

        <template #right>
          <v-field
            field="key_access"
            :value="status.key_access"
            :label="$t('key_access').capitalize()"
            :mode="mode"
            :property="property"
            type="html"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("important_notes").capitalize() }}
        </template>

        <template #right>
          <v-field
            field="important_notes"
            :label="$t('important_notes').capitalize()"
            :value="status.important_notes"
            :mode="mode"
            type="html"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <!-- Certificat bon état: Client (auto-filled by the "À louer" readiness
           answers, pencil re-asks the question) and Devloc (staff only), in
           one row -->
      <v-description-item>
        <template #left>
          {{ $t("good_condition_cert_label") }}
        </template>

        <template #right>
          <!-- Two aligned lines; the Oui/Non badge itself is the control
               (Client re-asks the readiness question, Devloc toggles) -->
          <div class="cert-row py-1">
            <div class="cert-line">
              <span class="is-size-7 has-text-grey cert-who">Client</span>
              <button
                type="button"
                class="tag is-size-7 cert-toggle"
                :class="status.client_good_condition ? 'is-success' : 'is-danger is-light'"
                :disabled="mode !== 'update'"
                :title="mode === 'update' ? $t('click_to_change') : null"
                @click="openClientCertModal"
              >
                {{ status.client_good_condition ? $t("yes").capitalize() : $t("no").capitalize() }}
              </button>
              <span
                v-if="status.client_good_condition_at"
                class="is-size-7 has-text-grey"
              >
                {{ $dayjs(status.client_good_condition_at).format("LL HH:mm") }}
              </span>
            </div>
            <div class="cert-line">
              <span class="is-size-7 has-text-grey cert-who">Devloc</span>
              <button
                type="button"
                class="tag is-size-7 cert-toggle"
                :class="status.devloc_good_condition ? 'is-success' : 'is-danger is-light'"
                :disabled="!canToggleDevlocCert"
                :title="canToggleDevlocCert ? $t('click_to_change') : null"
                @click="toggleDevlocCertification"
              >
                {{ status.devloc_good_condition ? $t("yes").capitalize() : $t("no").capitalize() }}
              </button>
              <span
                v-if="status.devloc_good_condition_at"
                class="is-size-7 has-text-grey"
              >
                {{ $dayjs(status.devloc_good_condition_at).format("LL HH:mm") }}
              </span>
            </div>
          </div>
        </template>
      </v-description-item>

      <v-description-item v-if="status.type == 'rented'">
        <template #left>
          {{ $t("starting_date_rented").capitalize() }}
        </template>

        <template #right>
          <v-field
            field="rent_start_date"
            :label="$t('starting_date_rented').capitalize()"
            :value="status.rent_start_date"
            mode="show"
            type="date"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <v-description-item>
        <template #left>
          {{ $t("rent_agent_name").capitalize() }}
        </template>

        <template #right>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between is-full"
          >
            <div
              class="is-flex is-align-items-center is-justify-content-space-between is-full content"
            >
              <!-- Every agent (there is room here, no "+N" like the list) -->
              <div
                v-if="status.rent_agents && status.rent_agents.length"
                class="agent-pile"
              >
                <span
                  v-for="agent in status.rent_agents"
                  :key="agent.id"
                >
                  <b-tooltip
                    type="is-black"
                    position="is-left"
                    multilined
                  >
                    <img
                      class="is-30 is-avatar"
                      :src="agent.avatar_url"
                      :alt="agent.name"
                    >
                    <template #content>
                      <div>
                        <p>{{ agent.name }}</p>
                        <p>{{ agent.email }}</p>
                      </div>
                    </template>
                  </b-tooltip>
                </span>
              </div>
              <span v-else> - </span>

              <button
                v-if="$roleCan([$laravel.role_admin]) && mode == 'update'"
                type="button"
                class="button is-white is-small"
                @click="showRentAgentPopup(status)"
              >
                <div class="icon">
                  <i class="mdi mdi-pencil mdi-14px" />
                </div>
              </button>
            </div>
          </div>
        </template>
      </v-description-item>

      <v-description-item
        v-if="
          $roleCan([$laravel.role_admin]) ||
            userExistInRentAgent($laravel.user.id, status)
        "
      >
        <template #left>
          {{ $t("agent_commission_fee").capitalize() }}
        </template>

        <template #right>
          <v-field
            :can-edit="$roleCan([$laravel.role_admin])"
            field="agent_commission_fee"
            :label="$t('agent_commission_fee').capitalize()"
            :value="status.agent_commission_fee"
            :mode="mode"
            type="money"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <v-description-item
        v-if="
          $roleCan([$laravel.role_admin]) ||
            userExistInRentAgent($laravel.user.id, status)
        "
      >
        <template #left>
          {{ $t("internal_notes").capitalize() }}
        </template>
        <template #right>
          <v-field
            :can-edit="$roleCan([$laravel.role_admin])"
            field="internal_notes"
            :label="$t('internal_notes').capitalize()"
            :value="status.internal_notes"
            :mode="mode"
            type="html"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>

      <v-description-item
        v-if="
          $roleCan([$laravel.role_admin]) ||
            userExistInRentAgent($laravel.user.id, status)
        "
      >
        <template #left>
          {{ $t("text_and_pictures").capitalize() }}
        </template>

        <template #right>
          <v-field
            :can-edit="$roleCan([$laravel.role_admin])"
            field="google_drive_url_link"
            :label="$t('google_drive_link_text').capitalize()"
            :value="status.google_drive_url_link"
            :mode="mode"
            type="url"
            :property="property"
            @click="onClick"
          />
        </template>
      </v-description-item>
      <template v-if="showAll">
        <v-description-item>
          <template #left>
            {{ $t("current_tenant_name").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="current_tenant_name"
              :label="$t('current_tenant_name').capitalize()"
              :value="status.current_tenant_name"
              :mode="mode"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("current_tenant_contact_email").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="current_tenant_contact_email"
              :label="$t('current_tenant_contact_email').capitalize()"
              :value="status.current_tenant_contact_email"
              :mode="mode"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("current_tenant_contact_phone").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="current_tenant_contact_phone"
              :label="$t('current_tenant_contact_phone').capitalize()"
              :value="status.current_tenant_contact_phone"
              :mode="mode"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("clause_g").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="clause_g"
              :label="$t('clause_g').capitalize()"
              :value="status.clause_g"
              :mode="mode"
              type="html"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("previous_rent").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="previous_rent"
              :label="$t('previous_rent').capitalize()"
              :value="status.previous_rent"
              :mode="mode"
              type="money"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("in_renovation_status").capitalize() }}
          </template>

          <template #right>
            <v-field
              v-if="status.type == 'in_renovation'"
              field="in_renovation_status"
              :label="
                $t('in_renovation_' + status.in_renovation_status).capitalize()
              "
              :value="status.in_renovation_status"
              :show-label="true"
              :mode="canEditRenovationFields(mode)"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("in_renovation_type").capitalize() }}
          </template>

          <template #right>
            <v-field
              v-if="status.type == 'in_renovation'"
              field="in_renovation_type"
              :label="$t('in_renovation_type').capitalize()"
              :value="$t(status.in_renovation_type).capitalize()"
              :mode="canEditRenovationFields(mode)"
              :property="property"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("in_renovation_description").capitalize() }}
          </template>

          <template #right>
            <v-field
              v-if="status.type == 'in_renovation'"
              field="in_renovation_description"
              :label="$t('in_renovation_description').capitalize()"
              :value="status.in_renovation_description"
              :mode="canEditRenovationFields(mode)"
              :property="property"
              type="html"
              @click="onClick"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("signed_documents_url").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="signed_documents_url"
              :label="$t('signed_documents_url').capitalize()"
              :value="status.signed_documents_url"
              :mode="'show'"
              type="url"
              :property="property"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("rental_notes").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="rental_notes"
              :label="$t('rental_notes').capitalize()"
              :value="status.rental_notes"
              :mode="'show'"
              :property="property"
              type="html"
            />
          </template>
        </v-description-item>
        <v-description-item>
          <template #left>
            {{ $t("repair_items").capitalize() }}
          </template>

          <template #right>
            <v-field
              field="repair_items"
              :label="$t('repair_items').capitalize()"
              :value="status.repair_items"
              :mode="'show'"
              :property="property"
              type="html"
            />
          </template>
        </v-description-item>
      </template>
    </v-description>

    <div class="has-text-centered">
      <button
        type="button"
        class="button is-ghost btn-show-more p-0"
        @click="toggleShowAll"
      >
        <span v-if="!showAll">
          {{ $t("show_more") }}
        </span>
        <span v-if="showAll">
          {{ $t("show_less") }}
        </span>
      </button>
    </div>

    <b-modal
      v-if="updatePayload && updatePayload.field"
      v-model="isOpen"
      :width="600"
      @close="closeModal"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-2">
              {{ $t("update_property_status_field") }}
              {{ updatePayload.label.toLowerCase() }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="closeModal"
            />
          </div>
        </header>
        <section class="modal-card-body">
          <v-form
            v-if="property"
            ref="form"
            :data="form"
            :url="
              $laravel.route('api.property.statuses.update_field_details', {
                property: property.id,
                field: updatePayload.field,
              })
            "
            :method="'patch'"
            @success="onFormSuccess"
          >
            <div
              v-if="fieldSchema"
              class="field"
            >
              <v-input-date
                v-if="fieldIs('vInputDate', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :value="fieldSchema.attributes.value"
                :label="updatePayload.label"
                :disabled="fieldSchema.attributes.disabled"
                :name="fieldSchema.name"
              />

              <v-input
                v-if="fieldIs('vInputEmail', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :name="fieldSchema.name"
                type="email"
                :label="updatePayload.label"
                :has-info="fieldSchema.attributes.has_info"
                :class="{
                  'custom-tooltip-position': fieldSchema.attributes.has_info,
                }"
                info-size="is-large"
                info-type="html"
              >
                <template
                  v-if="fieldSchema.attributes.has_info"
                  #info
                >
                  <p class="has-text-left">
                    {{ fieldSchema.attributes.text_info }}
                  </p>
                </template>
              </v-input>

              <v-input
                v-if="fieldIs('vInputText', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :name="fieldSchema.name"
                :label="updatePayload.label"
                type="text"
                :has-info="fieldSchema.attributes.has_info"
                :class="{
                  'custom-tooltip-position': fieldSchema.attributes.has_info,
                }"
                info-size="is-large"
                info-type="html"
              >
                <template
                  v-if="fieldSchema.attributes.has_info"
                  #info
                >
                  <p class="has-text-left">
                    {{ fieldSchema.attributes.text_info }}
                  </p>
                </template>
              </v-input>

              <v-input-switch
                v-if="fieldIs('vInputSwitch', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :true-value="true"
                :label="updatePayload.label"
                :false-value="false"
                label-position="right"
                :disabled="fieldSchema.attributes.disabled"
                :name="fieldSchema.name"
              />

              <v-input
                v-if="fieldIs('vInputNumber', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :label="updatePayload.label"
                :placeholder="placeholder(fieldSchema)"
                type="number"
                :disabled="fieldSchema.attributes.disabled"
                :name="fieldSchema.name"
                :has-info="fieldSchema.attributes.has_info"
                :class="{
                  'custom-tooltip-position': fieldSchema.attributes.has_info,
                }"
                info-size="is-large"
                info-type="html"
              >
                <template
                  v-if="fieldSchema.attributes.has_info"
                  #info
                >
                  <p class="has-text-left">
                    {{ fieldSchema.attributes.text_info }}
                  </p>
                </template>
              </v-input>

              <v-input-rich-text
                v-if="fieldIs('vInputRichText', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :disabled="fieldSchema.attributes.disabled"
                :label="updatePayload.label"
                :name="fieldSchema.name"
              />
              <v-input-select
                v-if="fieldIs('vInputSelect', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :disabled="fieldSchema.attributes.disabled"
                :label="updatePayload.label"
                :name="fieldSchema.name"
              >
                <option
                  v-for="option in fieldSchema.attributes.options"
                  :key="option.key"
                  :value="option.key"
                >
                  {{ option.label.capitalize() }}
                </option>
              </v-input-select>

              <v-input-tag
                v-if="fieldIs('vInputTag', fieldSchema)"
                v-model="form[fieldSchema.name]"
                :disabled="fieldSchema.attributes.disabled"
                :label="fieldSchema.label.capitalize()"
                :name="fieldSchema.name"
                field="label"
                :data="
                  filterTagsData(
                    fieldSchema.attributes.options,
                    fieldSchema.name
                  )
                "
                :open-on-focus="true"
              />
            </div>
            <footer class="mt-5">
              <b-button
                :label="$t('cancel').capitalize()"
                @click="closeModal"
              />
              <button
                class="button is-primary"
                type="submit"
              >
                {{ $t("save") }}
              </button>
            </footer>
          </v-form>
        </section>
      </div>
    </b-modal>
    <b-modal
      :active="currentlyEditedRendAgentPropertyId != null"
      :width="600"
      @close="onRentAgentModelClose()"
    >
      <div class="box">
        <v-form
          v-if="currentlyEditedRendAgentPropertyId"
          :data="formRentAgent"
          method="patch"
          :url="
            $laravel.route('api.property.update.rent_agent', {
              property: currentlyEditedRendAgentPropertyId,
            })
          "
          @success="onRentAgentModelClose"
        >
          <v-input-tag-fetch
            :value="rent_agents"
            name="rent_agent_id"
            :route="$laravel.route('api.users.employees')"
            :placeholder="$t('rent_agent_id').capitalize()"
            :label="$t('rent_agent_id').capitalize()"
            :open-on-focus="true"
            field="name"
            @input="onRentAgentsModelChange($event)"
          />

          <v-input
            v-model="formRentAgent.agent_commission_fee"
            type="number"
            name="agent_commission_fee"
          />

          <v-input
            v-model="formRentAgent.google_drive_url_link"
            type="text"
            name="google_drive_url_link"
          />

          <v-input-rich-text
            v-model="formRentAgent.internal_notes"
            name="internal_notes"
          />

          <button
            type="submit"
            class="button is-primary"
          >
            {{ $t("update") }}
          </button>
        </v-form>
      </div>
    </b-modal>

    <b-modal
      v-if="isOpenPossibleLeaseDurationModal"
      v-model="isOpenPossibleLeaseDurationModal"
      :width="600"
      @close="onClosePossibleLeaseDurationModal"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-2">
              {{ $t("update_property_status_field") }}
              {{ $t("status_possible_lease_duration").toLowerCase() }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="onClosePossibleLeaseDurationModal"
            />
          </div>
        </header>
        <!-- Tall enough that the open dropdown always shows every option -->
        <section class="modal-card-body lease-duration-modal-body">
          <v-form
            v-if="property"
            ref="form"
            :data="possibleLeaseDurationForm"
            :url="
              $laravel.route(
                'api.property.statuses.update_status_lease_duration',
                { property: property.id, propertyStatus: status.id }
              )
            "
            :method="'patch'"
            @success="onPossibleLeaseDurationFormSuccess"
          >
            <div class="field">
              <!-- Every option visible at once (like the status picker), so
                   Annuler/Enregistrer never disappear behind a dropdown -->
              <label class="label">
                {{ $t('status_possible_lease_duration').capitalize() }}
                <span class="multi-select-hint">({{ $t("selection_multiple") }})</span>
              </label>
              <div class="status-choice-list">
                <label
                  v-for="option in leaseDurationOptions"
                  :key="option.key"
                  class="status-choice"
                  :class="{ 'is-selected': selectedLeaseDurationKeys.includes(option.key) }"
                >
                  <input
                    type="checkbox"
                    class="status-choice-radio"
                    :checked="selectedLeaseDurationKeys.includes(option.key)"
                    @change="toggleLeaseDuration(option.key)"
                  >
                  <span class="status-choice-body">
                    <span class="status-choice-title">{{ option.label }}</span>
                  </span>
                </label>
              </div>

              <v-input
                v-if="isPossibleLeaseDurationOtherVisible"
                v-model="
                  possibleLeaseDurationForm.possible_lease_duration_other
                "
                type="text"
                :label="$t('other').capitalize()"
                name="possible_lease_duration_other"
              />
            </div>
            <footer class="mt-5">
              <b-button
                :label="$t('cancel').capitalize()"
                @click="onClosePossibleLeaseDurationModal"
              />
              <button
                class="button is-primary"
                type="submit"
              >
                {{ $t("save") }}
              </button>
            </footer>
          </v-form>
        </section>
      </div>
    </b-modal>

    <!-- Promotion editor -->
    <b-modal
      v-if="isOpenPromotionModal"
      v-model="isOpenPromotionModal"
      :width="600"
      @close="onClosePromotionModal"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-2">
              {{ $t("update_property_status_field") }}
              {{ $t("promotion").toLowerCase() }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="onClosePromotionModal"
            />
          </div>
        </header>
        <section class="modal-card-body">
          <v-form
            v-if="property"
            ref="promotionFormRef"
            :data="promotionForm"
            :url="
              $laravel.route('api.property.statuses.update_promotion', {
                property: property.id,
                propertyStatus: status.id,
              })
            "
            :method="'patch'"
            @success="onPromotionFormSuccess"
          >
            <v-input-select
              v-model="promotionForm.promotion_type"
              name="promotion_type"
              :label="$t('promotion_type').capitalize()"
              @input="onPromotionTypeChange"
            >
              <option :value="null">{{ $t("promotion_none") }}</option>
              <option value="monthly_credit">{{ $t("promotion_type_credit") }}</option>
              <option value="free_month">{{ $t("promotion_type_free_month") }}</option>
              <option value="other">{{ $t("promotion_type_other") }}</option>
            </v-input-select>

            <!-- Choix 1 : Crédit mensuel -->
            <template v-if="promotionForm.promotion_type === 'monthly_credit'">
              <v-input-number
                v-model="promotionForm.promotion_credit_amount"
                name="promotion_credit_amount"
                :label="$t('promotion_credit_label')"
              />
              <div class="notification is-light is-size-7 p-3 mt-2">
                {{ $t("promotion_credit_hint", { amount: freeMonthCreditHint }) }}
              </div>
            </template>

            <!-- Choix 2 : Mois gratuit fixe -->
            <v-input-select
              v-if="promotionForm.promotion_type === 'free_month'"
              v-model="promotionForm.promotion_free_month"
              name="promotion_free_month"
              :label="$t('promotion_free_month_label')"
            >
              <option value="">{{ $t("select").capitalize() }}</option>
              <option value="first">{{ $t("promotion_month_first") }}</option>
              <option value="second">{{ $t("promotion_month_second") }}</option>
              <option value="last">{{ $t("promotion_month_last") }}</option>
            </v-input-select>

            <!-- Choix 3 : Autre -->
            <v-input
              v-if="promotionForm.promotion_type === 'other'"
              v-model="promotionForm.promotion_text"
              type="textarea"
              :rows="4"
              name="promotion_text"
              :label="$t('promotion_type_other')"
            />

            <footer class="mt-5">
              <b-button
                :label="$t('cancel').capitalize()"
                @click="onClosePromotionModal"
              />
              <button
                class="button is-primary ml-2"
                type="submit"
              >
                {{ $t("save").capitalize() }}
              </button>
            </footer>
          </v-form>
        </section>
      </div>
    </b-modal>

    <b-modal
      :active="currentlyEditedRendAgentPropertyId != null"
      :width="600"
      @close="onRentAgentModelClose()"
    >
      <div class="box">
        <v-form
          v-if="currentlyEditedRendAgentPropertyId"
          :data="formRentAgent"
          method="patch"
          :url="
            $laravel.route('api.property.update.rent_agent', {
              property: currentlyEditedRendAgentPropertyId,
            })
          "
          @success="onRentAgentModelClose"
        >
          <v-input-tag-fetch
            :value="rent_agents"
            name="rent_agent_id"
            :route="$laravel.route('api.users.employees')"
            :placeholder="$t('rent_agent_id').capitalize()"
            :label="$t('rent_agent_id').capitalize()"
            :open-on-focus="true"
            field="name"
            @input="onRentAgentsModelChange($event)"
          />

          <v-input
            v-model="formRentAgent.agent_commission_fee"
            type="number"
            name="agent_commission_fee"
          />

          <v-input
            v-model="formRentAgent.google_drive_url_link"
            type="text"
            name="google_drive_url_link"
          />

          <v-input-rich-text
            v-model="formRentAgent.internal_notes"
            name="internal_notes"
          />

          <button
            type="submit"
            class="button is-primary"
          >
            {{ $t("update") }}
          </button>
        </v-form>
      </div>
    </b-modal>

    <b-modal
      v-if="isShownPriceRevisionModal"
      v-model="isShownPriceRevisionModal"
      :width="600"
      @close="onClosePriceRevisionModal"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-2">
              {{ $t("price_revision") }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="onClosePriceRevisionModal"
            />
          </div>
        </header>
        <section class="modal-card-body">
          <v-form
            v-if="property"
            ref="form"
            :data="priceRevisionForm"
            :url="
              $laravel.route(
                'api.property.statuses.price_revision',
                { property: property.id, propertyStatus: status.id }
              )
            "
            :method="'patch'"
            @success="onClosePriceRevisionModal"
          >
            <!-- 1. Loyer actuel (lecture seule) -->
            <div class="field">
              <label class="label">{{ $t("current_rent_label").capitalize() }}</label>
              <p>{{ status.asked_rent }} $</p>
            </div>

            <!-- 2. Promotion actuelle (lecture seule) -->
            <div class="field">
              <label class="label">{{ $t("current_promotion_label").capitalize() }}</label>
              <p style="white-space: pre-line">{{ promotionSummary }}</p>
            </div>

            <!-- 3. Loyer recommandé -->
            <div class="field">
              <v-input
                v-model="priceRevisionForm.rent"
                type="text"
                :label="$t('recommended_rent').capitalize()"
                name="rent"
              />
            </div>

            <!-- 4. Promotion recommandée (mêmes options que l'éditeur) -->
            <v-input-select
              v-model="priceRevisionForm.promotion_type"
              name="promotion_type"
              :label="$t('recommended_promotion').capitalize()"
              @input="onRevisionPromotionTypeChange"
            >
              <option :value="null">{{ $t("promotion_none") }}</option>
              <option value="monthly_credit">{{ $t("promotion_type_credit") }}</option>
              <option value="free_month">{{ $t("promotion_type_free_month") }}</option>
              <option value="other">{{ $t("promotion_type_other") }}</option>
            </v-input-select>

            <template v-if="priceRevisionForm.promotion_type === 'monthly_credit'">
              <v-input-number
                v-model="priceRevisionForm.promotion_credit_amount"
                name="promotion_credit_amount"
                :label="$t('promotion_credit_label')"
              />
            </template>

            <v-input-select
              v-if="priceRevisionForm.promotion_type === 'free_month'"
              v-model="priceRevisionForm.promotion_free_month"
              name="promotion_free_month"
              :label="$t('promotion_free_month_label')"
            >
              <option value="">{{ $t("select").capitalize() }}</option>
              <option value="first">{{ $t("promotion_month_first") }}</option>
              <option value="second">{{ $t("promotion_month_second") }}</option>
              <option value="last">{{ $t("promotion_month_last") }}</option>
            </v-input-select>

            <v-input
              v-if="priceRevisionForm.promotion_type === 'other'"
              v-model="priceRevisionForm.promotion_text"
              type="textarea"
              :rows="3"
              name="promotion_text"
              :label="$t('promotion_type_other')"
            />

            <!-- 5. Justification / commentaires -->
            <div class="field mt-4">
              <v-input-rich-text
                v-model="priceRevisionForm.description"
                type="text"
                :label="$t('notes_price_revision').capitalize()"
                name="description"
              />
            </div>
            <footer class="mt-5">
              <b-button
                :label="$t('cancel').capitalize()"
                @click="onClosePriceRevisionModal"
              />
              <button
                class="button is-primary"
                type="submit"
              >
                {{ $t("save") }}
              </button>
            </footer>
          </v-form>
        </section>
      </div>
    </b-modal>

    <!-- Client certification: re-ask the readiness question -->
    <b-modal
      v-model="isOpenClientCertModal"
      :width="600"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title title is-6 mb-0">
            {{ $t("client_good_condition_label") }}
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="isOpenClientCertModal = false"
          />
        </header>
        <section class="modal-card-body">
          <p
            class="label mb-3 has-text-weight-normal"
            v-html="status.is_property_occupied ? $t('recent_inspection_question') : $t('photo_ready_question')"
          />
          <div class="cert-choices">
            <b-radio
              v-model="clientCertChoice"
              :native-value="true"
              class="cert-choice"
            >
              <span v-html="status.is_property_occupied ? $t('recent_inspection_yes') : $t('photo_ready_yes')" />
            </b-radio>
            <b-radio
              v-model="clientCertChoice"
              :native-value="false"
              class="cert-choice"
            >
              <span v-html="status.is_property_occupied ? $t('recent_inspection_no') : $t('photo_ready_no')" />
            </b-radio>
          </div>

          <!-- Occupied + attested: same Travaux et inspection sub-flow as
               the status-change form -->
          <template v-if="status.is_property_occupied && clientCertChoice === true">
            <div class="cert-choices mt-4">
              <b-radio
                v-model="certInspectionMode"
                native-value="new"
                class="cert-choice"
              >
                {{ $t("inspection_mode_new") }}
              </b-radio>
              <b-radio
                v-if="certExistingInspections.length"
                v-model="certInspectionMode"
                native-value="existing"
                class="cert-choice"
              >
                {{ $t("inspection_mode_existing") }}
              </b-radio>
            </div>

            <b-field
              v-if="certInspectionMode === 'existing'"
              :label="$t('inspection_existing_label')"
              class="mt-3"
            >
              <b-select
                v-model="certExistingInspectionId"
                expanded
              >
                <option
                  v-for="insp in certExistingInspections"
                  :key="insp.id"
                  :value="insp.id"
                >
                  {{ insp.date }} — {{ (insp.elements || []).length }} {{ $t("inspection_elements_short") }}
                </option>
              </b-select>
            </b-field>

            <template v-if="certInspectionMode === 'new'">
              <b-field
                :label="$t('inspection_description_label')"
                class="mt-3"
              >
                <b-input
                  v-model="certInspectionDescription"
                  type="textarea"
                  rows="3"
                />
              </b-field>
              <b-field :label="$t('inspection_photos_label')">
                <b-upload
                  v-model="certPhotoFiles"
                  multiple
                  drag-drop
                  expanded
                  accept="image/*"
                  @input="onCertInspectionPhotos"
                >
                  <section class="section py-4">
                    <div class="content has-text-centered">
                      <b-icon
                        icon="camera"
                        size="is-medium"
                      />
                      <p class="is-size-7 mb-0">
                        {{ $t("drop_or_click_to_upload") }}
                      </p>
                    </div>
                  </section>
                </b-upload>
              </b-field>
              <div
                v-if="certInspectionPhotos.length"
                class="inspection-thumbs"
              >
                <img
                  v-for="(photo, photoIndex) in certInspectionPhotos"
                  :key="photoIndex"
                  :src="photo"
                  class="inspection-thumb"
                  :title="$t('delete')"
                  @click="certInspectionPhotos.splice(photoIndex, 1)"
                >
              </div>
            </template>
          </template>

          <footer class="mt-5 has-text-right">
            <b-button
              :label="$t('cancel').capitalize()"
              @click="isOpenClientCertModal = false"
            />
            <b-button
              type="is-primary"
              :disabled="clientCertChoice === null || (status.is_property_occupied && clientCertChoice === true && certInspectionMode === 'existing' && !certExistingInspectionId)"
              :loading="certSaving"
              @click="saveClientCertification"
            >
              {{ $t("save") }}
            </b-button>
          </footer>
        </section>
      </div>
    </b-modal>

    <!-- Devloc certification: simple staff-only toggle -->
    <b-modal
      v-model="isOpenDevlocCertModal"
      :width="480"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title title is-6 mb-0">
            {{ $t("devloc_good_condition_label") }}
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="isOpenDevlocCertModal = false"
          />
        </header>
        <section class="modal-card-body">
          <b-switch v-model="devlocCertChoice">
            {{ $t("devloc_good_condition_switch") }}
          </b-switch>
          <footer class="mt-5 has-text-right">
            <b-button
              :label="$t('cancel').capitalize()"
              @click="isOpenDevlocCertModal = false"
            />
            <b-button
              type="is-primary"
              :loading="certSaving"
              @click="saveCertification({ devloc: devlocCertChoice })"
            >
              {{ $t("save") }}
            </b-button>
          </footer>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { i18n } from "@/services/i18n";
import BaseDescriptionList from "./BaseDescriptionList.vue";
import BaseDescriptionListItem from "./BaseDescriptionListItem.vue";
import UpdatePropertyStatusFieldField from "./UpdatePropertyStatusFieldField.vue";

export default {
  components: {
    "v-description": BaseDescriptionList,
    "v-description-item": BaseDescriptionListItem,
    "v-field": UpdatePropertyStatusFieldField,
  },
  props: {
    status: {
      default: null,
      type: Object,
    },
    property: {
      default: null,
      type: Object,
    },
    mode: {
      default: "update",
      type: String,
    },
  },
  data() {
    return {
      isOpen: false,
      fieldSchema: null,
      form: {},
      updatePayload: {},
      currentlyEditedRendAgentPropertyId: null,
      isShownPriceRevisionModal: false,
      priceRevisionForm: {
        rent: null,
        promotion_type: null,
        promotion_credit_amount: null,
        promotion_free_month: null,
        promotion_text: null,
        description: null,
      },
      formRentAgent: {
        agent_commission_fee: null,
        google_drive_url_link: null,
        internal_notes: null,
        rent_agent_ids: [],
      },
      rent_agents: [],
      possibleLeaseDurationForm: {
        possible_lease_duration: [],
        possible_lease_duration_other: null,
      },
      showAll: false,
      isOpenPossibleLeaseDurationModal: false,
      isOpenClientCertModal: false,
      isOpenDevlocCertModal: false,
      clientCertChoice: null,
      devlocCertChoice: false,
      certSaving: false,
      certInspectionMode: "new",
      certExistingInspections: [],
      certExistingInspectionId: null,
      certInspectionDescription: null,
      certInspectionPhotos: [],
      certPhotoFiles: [],
      isOpenPromotionModal: false,
      promotionForm: {
        promotion_type: null,
        promotion_credit_amount: null,
        promotion_free_month: null,
        promotion_text: null,
      },
    };
  },
  computed: {
    canToggleDevlocCert() {
      return (
        this.mode === "update" &&
        this.$roleCan([this.$laravel.role_admin, this.$laravel.role_employee])
      );
    },
    latestPropertyInspection() {
      if (
        this.property &&
        this.property.latest_property_inspection &&
        this.property.latest_property_inspection.date
      ) {
        return this.property.latest_property_inspection.date;
      }
      return i18n.t("empty_inspections");
    },

    leaseDurationOptions() {
      return window.Laravel.status_possible_lease_duration || [];
    },
    // The form stores [{key, label}] (backend format); the dropdown works
    // with plain keys
    selectedLeaseDurationKeys: {
      get() {
        return (this.possibleLeaseDurationForm.possible_lease_duration || []).map(
          (item) => item.key
        );
      },
      set(keys) {
        this.possibleLeaseDurationForm.possible_lease_duration =
          this.leaseDurationOptions.filter((option) => keys.includes(option.key));
        this.handlePossibleLeaseDurationInput(
          this.possibleLeaseDurationForm.possible_lease_duration
        );
      },
    },
    leaseDurationTriggerLabel() {
      const selected = this.possibleLeaseDurationForm.possible_lease_duration || [];

      if (!selected.length) {
        return this.$t("select").capitalize();
      }
      if (selected.length === 1) {
        return selected[0].label;
      }

      return this.$t("selections_count", { count: selected.length });
    },
    isPossibleLeaseDurationOtherVisible() {
      if (
        !this.possibleLeaseDurationForm.possible_lease_duration ||
        !this.possibleLeaseDurationForm.possible_lease_duration.length
      ) {
        return false;
      }

      return this.possibleLeaseDurationForm.possible_lease_duration.some(
        (item) => item.key === "lease_duration_other"
      );
    },
    // Possible lease durations, one label per line ("-" when none)
    leaseDurationLines() {
      const items = (this.status && this.status.possible_lease_duration) || [];
      if (!items.length) {
        return ["-"];
      }
      return items.map((item) =>
        item.key == this.$laravel.status_lease_duration_other
          ? this.$t("other") + ": (" + (this.status.possible_lease_duration_other || "") + ")"
          : this.$t(item.key)
      );
    },
    // Human-readable summary of the current promotion (shown in the details).
    promotionSummary() {
      const s = this.status;
      if (!s || !s.promotion_type) {
        return "-";
      }
      if (s.promotion_type === "monthly_credit") {
        return this.$t("promotion_type_credit") + " : " + s.promotion_credit_amount + "$";
      }
      if (s.promotion_type === "free_month") {
        return (
          this.$t("promotion_type_free_month") +
          " (" +
          this.$t("promotion_month_" + s.promotion_free_month) +
          ")"
        );
      }
      return s.promotion_text || "-";
    },
    // Auto-calc hint: 1 free month ≈ rent / 12 (nearest dollar).
    freeMonthCreditHint() {
      const rent =
        this.status && this.status.asked_rent
          ? parseFloat(this.status.asked_rent)
          : 0;
      return Math.round(rent / 12);
    },
  },
  methods: {
    // Same pale color code as the properties list: green = À louer,
    // yellow = En application, blue = Loué, red = En rénovation / En attente
    statusTagClass(status) {
      if (status.type === "in_renovation") {
        return status.in_renovation_status === "pending" ? "is-pending" : "is-in-renovation";
      }

      return { for_rent: "is-for-rent", in_application: "is-in-application", rented: "is-rented" }[status.type] || "";
    },
    onClosePriceRevisionModal() {
      this.isShownPriceRevisionModal = false;
      this.priceRevisionForm.rent = null;
      this.priceRevisionForm.promotion_type = null;
      this.priceRevisionForm.promotion_credit_amount = null;
      this.priceRevisionForm.promotion_free_month = null;
      this.priceRevisionForm.promotion_text = null;
      this.priceRevisionForm.description = null;
      this.$emit("fetch", {});
    },
    toggleLeaseDuration(key) {
      const keys = this.selectedLeaseDurationKeys.slice();
      const index = keys.indexOf(key);

      if (index === -1) {
        keys.push(key);
      } else {
        keys.splice(index, 1);
      }
      this.selectedLeaseDurationKeys = keys;
    },
    openClientCertModal() {
      this.clientCertChoice = this.status.client_good_condition ? true : null;
      this.certInspectionMode = "new";
      this.certExistingInspectionId = null;
      this.certInspectionDescription = null;
      this.certInspectionPhotos = [];
      this.certPhotoFiles = [];
      this.isOpenClientCertModal = true;

      if (this.status.is_property_occupied && !this.certExistingInspections.length) {
        this.$http
          .get(`/api/properties/${this.property.id}/property-inspections`, {
            params: { per_page: 10 },
          })
          .then((response) => {
            this.certExistingInspections = response.data.data || [];
          })
          .catch(() => {});
      }
    },
    onCertInspectionPhotos(files) {
      const readers = (files || []).map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      );

      Promise.all(readers).then((results) => {
        this.certInspectionPhotos = [
          ...this.certInspectionPhotos,
          ...results.filter(Boolean),
        ];
        this.certPhotoFiles = [];
      });
    },
    saveClientCertification() {
      const payload = { client: this.clientCertChoice };

      if (this.status.is_property_occupied && this.clientCertChoice === true) {
        payload.inspection_mode = this.certInspectionMode;
        payload.existing_inspection_id =
          this.certInspectionMode === "existing" ? this.certExistingInspectionId : null;
        payload.inspection_description = this.certInspectionDescription;
        payload.inspection_photos = this.certInspectionPhotos;
      }

      this.saveCertification(payload);
    },
    openDevlocCertModal() {
      this.devlocCertChoice = !!this.status.devloc_good_condition;
      this.isOpenDevlocCertModal = true;
    },
    // Oui <-> Non straight from the badge (staff only)
    toggleDevlocCertification() {
      if (!this.canToggleDevlocCert || this.certSaving) {
        return;
      }
      this.saveCertification({ devloc: !this.status.devloc_good_condition });
    },
    saveCertification(payload) {
      this.certSaving = true;
      this.$http
        .patch(
          this.$laravel.route("api.property.statuses.certifications", {
            property: this.property.id,
            propertyStatus: this.status.id,
          }),
          payload
        )
        .then(() => {
          this.isOpenClientCertModal = false;
          this.isOpenDevlocCertModal = false;
          this.$emit("fetch");
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.certSaving = false;
        });
    },
    onPriceRevisionBtnClick() {
      // Recommended promotion starts from the current one, so saving without
      // touching it keeps the status unchanged.
      this.priceRevisionForm = {
        rent: null,
        promotion_type: this.status.promotion_type || null,
        promotion_credit_amount: this.status.promotion_credit_amount || null,
        promotion_free_month: this.status.promotion_free_month || null,
        promotion_text: this.status.promotion_text || null,
        description: null,
      };
      this.isShownPriceRevisionModal = true;
    },
    // Clear the fields that don't belong to the newly selected recommended type
    onRevisionPromotionTypeChange(type) {
      if (type !== "monthly_credit") {
        this.priceRevisionForm.promotion_credit_amount = null;
      }
      if (type !== "free_month") {
        this.priceRevisionForm.promotion_free_month = null;
      }
      if (type !== "other") {
        this.priceRevisionForm.promotion_text = null;
      }
    },
    userExistInRentAgent(user_id, status) {
      return status.rent_agents.some((agent) => agent.id == user_id);
    },
    onClosePossibleLeaseDurationModal() {
      this.isOpenPossibleLeaseDurationModal = false;
      this.possibleLeaseDurationForm.possible_lease_duration = [];
      this.possibleLeaseDurationForm.possible_lease_duration_other = null;
    },
    onOpenPromotionModal() {
      this.promotionForm = {
        promotion_type: this.status.promotion_type || null,
        promotion_credit_amount: this.status.promotion_credit_amount || null,
        promotion_free_month: this.status.promotion_free_month || null,
        promotion_text: this.status.promotion_text || null,
      };
      this.isOpenPromotionModal = true;
    },
    onClosePromotionModal() {
      this.isOpenPromotionModal = false;
    },
    // Clear the fields that don't belong to the newly selected type so stale
    // values aren't submitted (and can't trip validation, e.g. credit > rent).
    onPromotionTypeChange(type) {
      if (type !== "monthly_credit") {
        this.promotionForm.promotion_credit_amount = null;
      }
      if (type !== "free_month") {
        this.promotionForm.promotion_free_month = null;
      }
      if (type !== "other") {
        this.promotionForm.promotion_text = null;
      }
    },
    onPromotionFormSuccess(response) {
      this.isOpenPromotionModal = false;
      this.$emit("fetch", response);
    },

    onOpenPossibleLeaseDurationModal() {
      this.isOpenPossibleLeaseDurationModal = true;
      this.possibleLeaseDurationForm.possible_lease_duration =
        this.status.possible_lease_duration;
      this.possibleLeaseDurationForm.possible_lease_duration_other =
        this.status.possible_lease_duration_other;
    },
    handlePossibleLeaseDurationInput(value) {
      if (!value || !value.length) {
        return;
      }

      if (!value.some((item) => item.key === "lease_duration_other")) {
        this.possibleLeaseDurationForm.possible_lease_duration_other = null;
      }
    },
    onPossibleLeaseDurationFormSuccess(response) {
      this.isOpenPossibleLeaseDurationModal = false;
      this.possibleLeaseDurationForm.possible_lease_duration =
        this.status.possible_lease_duration;
      this.possibleLeaseDurationForm.possible_lease_duration_other =
        this.status.possible_lease_duration_other;
      this.$emit("fetch", {});
    },
    filterTagsData(data, field) {
      if (!this.form[field]) {
        return data;
      }
      return data.filter(
        (tag) => !this.form[field].some((selected) => selected.key === tag.key)
      );
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
    showRentAgentPopup(status) {
      this.currentlyEditedRendAgentPropertyId = status.property_id;
      this.formRentAgent.google_drive_url_link = status.google_drive_url_link;
      this.formRentAgent.agent_commission_fee = status.agent_commission_fee;
      this.formRentAgent.internal_notes = status.internal_notes;

      console.log(status);
      if (status.rent_agents && status.rent_agents.length) {
        this.rent_agents = status.rent_agents;
        this.formRentAgent.rent_agent_ids = status.rent_agents.map(
          (item) => item.id
        );
      }
    },
    onRentAgentsModelChange(value) {
      if (value && value.length) {
        this.formRentAgent.rent_agent_ids = value.map((item) => item.id);
        this.rent_agents = value;
      } else {
        this.formRentAgent.rent_agent_ids = [];
        this.rent_agents = [];
      }
    },
    onRentAgentModelClose() {
      this.currentlyEditedRendAgentPropertyId = null;
      this.formRentAgent.agent_commission_fee = null;
      this.formRentAgent.google_drive_url_link = null;
      this.formRentAgent.internal_notes = null;
      this.formRentAgent.rent_agent_ids = [];
      this.rent_agents = [];
      this.$emit("fetch", {});
    },
    onSuccessUpdateField(response) {
      this.$emit("fetch", response);
    },
    canEditRenovationFields(mode) {
      if (this.status.type == "in_renovation") {
        return mode;
      }

      if (this.status.type != "in_renovation") {
        return "show";
      }
    },
    getFieldSchema(payload) {
      this.$http
        .get(
          this.$laravel.route("api.property.statuses.get_field_details", {
            property: this.property.id,
            field: payload.field,
          })
        )
        .then((response) => {
          this.fieldSchema = response.data.data;
        });
    },
    onClick(payload) {
      this.updatePayload = payload;
      this.form[payload.field] = payload.value;
      this.getFieldSchema(payload);
      this.isOpen = true;
    },

    closeModal() {
      this.isOpen = false;
      this.updatePayload = {};
      this.form = {};
    },
    fieldIs(type, field) {
      return type == field.component;
    },
    placeholder(field) {
      if (field.attributes.placeholder) {
        return field.attributes.placeholder.capitalize();
      }
      return field.label.capitalize();
    },
    onFormSuccess(response) {
      this.$emit("fetch", response);
      this.closeModal();
    },
  },
};
</script>
<style scoped>
.btn-show-more,
.btn-show-more:focus,
.btn-show-more:hover {
  box-shadow: none;
  font-size: 14px;
  font-weight: 600;
}
.custom-tag {
  height: 20px;
  width: 20px;
  font-size: 10px;
}

/* Choice cards (same look as the status picker in the popup) */
.status-choice-list {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  overflow: hidden;
}

.status-choice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border-bottom: 1px solid #dbdbdb;
  cursor: pointer;
}

.status-choice:last-child {
  border-bottom: none;
}

.status-choice.is-selected {
  background: #eef1f7;
}

.status-choice-radio {
  margin-top: 4px;
  accent-color: #131627;
  flex-shrink: 0;
}

.status-choice-title {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #363636;
}

.status-choice.is-selected .status-choice-title {
  color: #131627;
}

/* Inspection photos in the certification modal */
.inspection-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.inspection-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dbdbdb;
  cursor: pointer;
}

.inspection-thumb:hover {
  outline: 2px solid #e02b2b;
}

/* Certification modal radios: full-sentence labels, stacked */
.cert-choices {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cert-choice {
  align-items: flex-start;
}

/* "Certificat bon état": Client / Devloc lines with Oui/Non aligned */
.cert-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cert-line {
  display: grid;
  grid-template-columns: 52px auto 1fr;
  align-items: center;
  gap: 10px;
}
.cert-toggle {
  border: 0;
  cursor: pointer;
  min-width: 52px;
  justify-content: center;
}
.cert-toggle:disabled {
  cursor: default;
  opacity: 1;
}
.cert-toggle:not(:disabled):hover {
  filter: brightness(0.94);
}

/* Multi-select dropdown, same look as the public TYPE filter */
.multi-select-hint {
  font-weight: 400;
  font-size: 0.75rem;
  color: #7a7a7a;
  text-transform: none;
  letter-spacing: normal;
}

.multi-select-trigger .multi-select-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multi-select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.multi-select-option .multi-select-check {
  margin-left: 0.75rem;
  color: inherit;
}

.multi-select-dropdown ::v-deep .dropdown-item {
  padding-right: 1rem;
}

</style>
